import * as React from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/**
 * 3D multi-select dropdown — geen search-input, gewoon checkbox-lijst.
 * Used voor Type order, Status, Leverancier filters.
 */
export interface Filter3DDropdownProps {
  label: string;
  options: readonly string[];
  values: string[];
  onChange: (next: string[]) => void;
  /** Optionele kleur-mapping per waarde voor de selected pills. */
  pillColor?: (value: string) => string;
  /** Compactere weergave (geen pills binnen trigger, alleen count). */
  triggerCountOnly?: boolean;
  /** Toon zoekbalk binnen de open dropdown (case-insensitive substring). */
  searchable?: boolean;
  /** Placeholder voor zoekbalk. */
  searchPlaceholder?: string;
  /** Tekst voor lege zoekresultaten. */
  emptySearchText?: string;
  className?: string;
}

const surface3D =
  "ring-1 ring-slate-300/60 bg-gradient-to-b from-white to-slate-100 " +
  "shadow-[0_2px_4px_rgba(0,0,0,0.10),inset_0_1px_0_rgba(255,255,255,0.7),inset_0_-1px_0_rgba(0,0,0,0.06)]";

export const Filter3DDropdown: React.FC<Filter3DDropdownProps> = ({
  label,
  options,
  values,
  onChange,
  pillColor,
  searchable = false,
  searchPlaceholder = "Zoeken...",
  emptySearchText = "Geen resultaten gevonden",
  className,
}) => {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");

  const filteredOptions = React.useMemo(() => {
    if (!searchable || !search.trim()) return options;
    const q = search.trim().toLowerCase();
    return options.filter((o) => o.toLowerCase().includes(q));
  }, [options, search, searchable]);

  React.useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  const toggle = (v: string) => {
    if (values.includes(v)) onChange(values.filter((x) => x !== v));
    else onChange([...values, v]);
  };

  const clearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  };

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "relative inline-flex items-center justify-between gap-2 min-w-[200px] h-10 px-3 rounded-full",
              "text-sm font-medium text-slate-700 transition-all duration-150",
              "hover:-translate-y-0.5 hover:shadow-[0_4px_8px_rgba(0,0,0,0.12),inset_0_1px_0_rgba(255,255,255,0.7)]",
              surface3D,
            )}
          >
            <span className="flex items-center gap-2 truncate">
              {label}
              {values.length > 0 && (
                <span className="inline-flex items-center justify-center rounded-full bg-slate-700 text-white text-xs font-semibold h-5 min-w-[1.25rem] px-1.5">
                  {values.length}
                </span>
              )}
            </span>
            <span className="flex items-center gap-1">
              {values.length > 0 && (
                <span
                  role="button"
                  onClick={clearAll}
                  className="inline-flex items-center justify-center h-5 w-5 rounded-full hover:bg-slate-200"
                  title="Filter wissen"
                >
                  <X className="h-3 w-3" />
                </span>
              )}
              <ChevronDown className={cn("h-4 w-4 transition-transform", open && "rotate-180")} />
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent
          className={cn("w-[260px] p-2 rounded-xl border-0", surface3D)}
          align="start"
        >
          {searchable && (
            <div className="mb-2">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={searchPlaceholder}
                autoFocus
                className={cn(
                  "w-full h-9 px-3 rounded-full text-sm text-slate-700 outline-none",
                  "placeholder:text-slate-400 focus:ring-2 focus:ring-slate-400/40",
                  surface3D,
                )}
              />
            </div>
          )}
          <ul className="max-h-[280px] overflow-auto">
            {options.length === 0 ? (
              <li className="px-3 py-2 text-sm text-muted-foreground">Geen opties</li>
            ) : filteredOptions.length === 0 ? (
              <li className="px-3 py-2 text-sm text-muted-foreground">{emptySearchText}</li>
            ) : (
              filteredOptions.map((opt) => {
                const checked = values.includes(opt);
                return (
                  <li key={opt}>
                    <button
                      type="button"
                      onClick={() => toggle(opt)}
                      className={cn(
                        "w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-left",
                        "hover:bg-slate-200/70 transition-colors",
                      )}
                    >
                      <span
                        className={cn(
                          "inline-flex items-center justify-center h-4 w-4 rounded border ring-1",
                          checked
                            ? "bg-slate-700 border-slate-700 ring-slate-400/50 text-white"
                            : "bg-white border-slate-300 ring-slate-200",
                        )}
                      >
                        {checked && <Check className="h-3 w-3" />}
                      </span>
                      <span className="flex-1 truncate">{opt}</span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </PopoverContent>
      </Popover>

      {values.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {values.map((v) => (
            <span
              key={v}
              className={cn(
                "relative inline-flex items-center gap-1 h-6 px-2 rounded-full text-xs font-semibold ring-1",
                "shadow-[0_2px_3px_rgba(0,0,0,0.15),inset_0_1px_0_rgba(255,255,255,0.35),inset_0_-1px_0_rgba(0,0,0,0.15)]",
                pillColor ? pillColor(v) : "bg-slate-200 text-slate-800 ring-slate-300/60",
              )}
            >
              <span className="relative">{v}</span>
              <button
                type="button"
                onClick={() => toggle(v)}
                className="relative inline-flex items-center justify-center h-4 w-4 rounded-full hover:bg-black/15"
                aria-label={`Verwijder ${v}`}
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default Filter3DDropdown;
